import { useCallback, useEffect, useState } from 'react'
import { CalendarClock, Loader2, Search, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { lookupPosAppointments } from '@/lib/api/sales'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'
import {
  posAppointmentGuestLabel,
  posAppointmentServiceLabel,
  posAppointmentSummary,
} from '@/lib/pos/posAppointment'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(appointmentId: number) => Promise<boolean>} props.onLoad
 * @param {boolean} props.submitting
 * @param {string} [props.error]
 */
export function PosAppointmentModal({
  open,
  onClose,
  onLoad,
  submitting,
  error = '',
}) {
  const [query, setQuery] = useState('')
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(false)
  const [localError, setLocalError] = useState('')
  const [selectedId, setSelectedId] = useState(null)

  const today = new Date().toISOString().slice(0, 10)

  const loadAppointments = useCallback(async (search) => {
    setLoading(true)
    setLocalError('')

    try {
      const items = await lookupPosAppointments({
        date: today,
        q: search.trim() || undefined,
      })
      setAppointments(items)
    } catch (err) {
      setLocalError(apiErrorMessage(err, 'Unable to load appointments.'))
      setAppointments([])
    } finally {
      setLoading(false)
    }
  }, [today])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setSelectedId(null)
      setLocalError('')
      return
    }

    loadAppointments('')
  }, [loadAppointments, open])

  useEffect(() => {
    if (!open) return undefined

    const timer = window.setTimeout(() => {
      loadAppointments(query)
    }, 250)

    return () => window.clearTimeout(timer)
  }, [loadAppointments, open, query])

  const handleLoad = useCallback(async () => {
    if (!selectedId) return
    const loaded = await onLoad(selectedId)
    if (loaded) {
      onClose()
    }
  }, [onClose, onLoad, selectedId])

  if (!open) return null

  const displayError = error || localError

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={submitting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-appointment-title"
        className="card-surface flex max-h-[min(80vh,640px)] w-full max-w-lg flex-col overflow-hidden shadow-luxury-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-border/50 px-4 py-3">
          <div>
            <p id="pos-appointment-title" className="flex items-center gap-2 text-sm font-semibold">
              <CalendarClock className="h-4 w-4 text-primary" />
              Load appointment
            </p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {SALON_DESK_CHECKOUT.lite
                ? "Today's bookings for this branch."
                : "Today's bookings · F3 shortcut"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-border/50 px-4 py-3">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              disabled={submitting}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search guest, phone, or stylist…"
              className="w-full rounded-xl border border-input bg-background py-2 pl-9 pr-3 text-sm"
            />
          </label>
        </div>

        {displayError ? (
          <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
            {displayError}
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading appointments…
            </div>
          ) : appointments.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No loadable appointments for today.
            </p>
          ) : (
            <ul className="space-y-2">
              {appointments.map((appointment) => {
                const selected = selectedId === appointment.id

                return (
                  <li key={appointment.id}>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => setSelectedId(appointment.id)}
                      className={cn(
                        'w-full rounded-xl border px-3 py-3 text-left transition-colors',
                        selected
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border/60 bg-card hover:bg-muted/30',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-foreground">
                            {posAppointmentGuestLabel(appointment)}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {posAppointmentServiceLabel(appointment)}
                          </p>
                          <p className="mt-1 text-[11px] font-medium text-primary">
                            {posAppointmentSummary(appointment)}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-lg bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                          {appointment.status?.replace('_', ' ')}
                        </span>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border/50 px-4 py-3">
          <Button type="button" variant="outline" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!selectedId || submitting} onClick={handleLoad}>
            {submitting ? 'Loading…' : 'Load to ticket'}
          </Button>
        </div>
      </div>
    </div>
  )
}
